<template>
  <div>
    <v-row justify="space-between" v-if="search">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by title'"
          :value="'title'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col cols="auto">
        <v-btn
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
          :to="{ name: 'CreateCampaign' }"
        >
          <v-icon color="black" small class="mr-2">
            $vuetify.icons.plus
          </v-icon>
          Create
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      class="mt-5"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      id="campaign-values"
    >
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <!-- Edit -->
          <v-tooltip bottom v-if="item.status === 'DRAFT' || item.status === 'PUBLISHED'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemForEdit = item;
                    getCampaignApplicantsForEdit(item);
                    editDialog = true;
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <!-- Unpublish -->
          <v-tooltip bottom v-if="item.status === 'PUBLISHED'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="(itemForUnpublish = item), (unpublishDialog = true)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-upload-off
                </v-icon>
              </v-btn>
            </template>
            {{ $t('unpublish.message') }}
          </v-tooltip>
          <!-- Delete -->
          <v-tooltip bottom v-if="item.status === 'DRAFT' || item.status === 'COMPLETED'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemToBeDeleted = item;
                    getCampaignApplicants(item);
                    deleteDialog = true;
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('delete.message') }}
          </v-tooltip>
          <!-- Copy -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="copyCampaignData(item)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            {{ $t('copy-message') }}
          </v-tooltip>
        </v-layout>
      </template>
      <template v-slot:item.headline="{ item }">
        <div>
          {{ item.headline }}
        </div>
      </template>
      <template v-slot:item.title="{ item }">
        <div>
          {{ item.title }}
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <div>
          {{ item.status }}
        </div>
      </template>
      <template v-slot:item.dateFrom="{ item }">
        <div>
          {{ formatDateString(item.dateFrom) }}
        </div>
      </template>
      <template v-slot:item.dateTo="{ item }">
        <div>
          {{ formatDateString(item.dateTo) }}
        </div>
      </template>
    </v-data-table>

    <!-- Dialog for Delete Campaign -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="675px">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          {{ itemToBeDeleted.headline }}
        </v-card-title>
        <v-card-text>
          {{ $t('delete.confirm') }}
        </v-card-text>
        <v-card-text v-if="hasApplicants">
          There are related campaign applicants with this campaign ID.
        </v-card-text>
        <v-card-actions v-if="hasApplicants">
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="deleteDialog = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="deleteCampaignWithApplicant(itemToBeDeleted.id)"
              >
                {{ $t('delete.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
        <v-card-actions v-else>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="deleteDialog = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="deleteCampaign(itemToBeDeleted.id)"
              >
                {{ $t('delete.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Edit -->
    <v-dialog v-if="itemForEdit !== null && hasApplicants" v-model="editDialog" max-width="675px">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          Edit Campaign
          <span style="color: red; font-weight: bold;" class="mx-2">
            {{ itemForEdit.id }}
          </span>
        </v-card-title>
        <v-card-text>
          You cannot edit the Campaign
          <span style="color: red; font-weight: bold;"> {{ itemForEdit.id }} </span> because it is
          associated with one or more applicants. You may delete the applicant(s) and edit the
          campaign, or copy the campaign and edit the copied one.
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog When Published -->
    <v-dialog
      v-if="itemForEdit !== null && !hasApplicants"
      v-model="editDialogWhenPublished"
      max-width="675px"
    >
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          Edit Campaign
          <span style="color: red; font-weight: bold;" class="mx-2"> {{ itemForEdit.id }} </span>
        </v-card-title>
        <v-card-text>
          You cannot edit the Campaign because it is
          <span style="color: red; font-weight: bold;"> PUBLISHED </span>. You need to unpublish
          first and then edit.
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="editDialogWhenPublished = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="unpublishCampaignFromEdit(itemForEdit.id)"
              >
                {{ $t('unpublish.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Unpublish Campaign -->
    <v-dialog v-if="itemForUnpublish !== null" v-model="unpublishDialog" max-width="675px">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          {{ itemForUnpublish.headline }}
        </v-card-title>
        <v-card-text>
          {{ $t('unpublish.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="unpublishDialog = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="unpublish(itemForUnpublish.id)"
              >
                {{ $t('unpublish.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import SearchText from '@/components/shared/search/SearchText';

export default {
  name: 'TableCampaign',
  components: {
    SearchText,
  },
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
  },
  data: () => ({
    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'ID', value: 'id' },
      { text: 'Headline', value: 'headline', width: 300 },
      { text: 'Title', value: 'title', width: 300 },
      { text: 'Status', value: 'status' },
      { text: 'Date From', value: 'dateFrom' },
      { text: 'Date To', value: 'dateTo' },
    ],
    deleteDialog: false,
    unpublishDialog: false,
    itemForUnpublish: null,
    itemForEdit: null,
    editDialog: false,
    editDialogWhenPublished: false,
    itemToBeDeleted: null,
    items: [],
    totalItems: 0,
    firstLoad: true,
    options: {},
    hasApplicants: false,
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
    this.getCampaignApplicants();
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }
      return query;
    },
    formatDateString(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },
    unpublish(campaignId) {
      EventBus.$emit('waiting', true);

      const payload = {
        status: 'DRAFT',
      };

      this.axios
        .put(`/api/v1/campaign/${campaignId}`, payload)
        .then(response => {
          console.log('Response:', response.data);
          EventBus.$emit('snackbar', {
            text: 'Unpublished Successfully',
          });
          this.unpublishDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log('Error:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    getCampaignApplicants(item) {
      if (item) {
        const id = item.id;
        this.axios
          .get(`api/v1/campaign/${id}`)
          .then(response => {
            console.log('Campaign Applicants:', response.data);

            this.campaignApplicants = response.data;
            this.hasApplicants = response.data.hasApplicants;
          })
          .catch(error => {
            console.error('Error fetching campaign applicants:', error);
          });
      }
    },
    getCampaignApplicantsForEdit(item) {
      if (item) {
        const id = item.id;
        this.axios
          .get(`api/v1/campaign/${id}`)
          .then(response => {
            console.log('Campaign Applicants:', response.data);

            this.campaignApplicants = response.data;
            this.hasApplicants = response.data.hasApplicants;
            if (this.hasApplicants === false) {
              const status = response.data.status;
              if (status === 'DRAFT') {
                this.$router.push({ name: 'EditCampaign', params: { id } });
              } else {
                this.editDialogWhenPublished = true;
              }
            }
          })
          .catch(error => {
            console.error('Error fetching campaign applicants:', error);
          });
      }
    },
    deleteCampaign() {
      if (this.itemToBeDeleted.id) {
        const id = this.itemToBeDeleted.id;
        if (this.hasApplicants) {
          this.deleteDialog = true;
          return;
        } else {
          EventBus.$emit('waiting', true);

          this.axios
            .delete(`api/v1/campaign/${id}`)
            .then(response => {
              console.log('Campaign deleted successfully:', response.data);
              EventBus.$emit('snackbar', {
                text: 'Deleted Successfully',
              });
              this.deleteDialog = false;
              this.getDataFromApi().then(data => {
                this.items = data.items;
                this.totalItems = data.total;
              });
            })
            .catch(error => {
              console.error('Error deleting campaign:', error);
            })
            .finally(() => {
              EventBus.$emit('waiting', false);
            });
        }
      }
    },
    deleteCampaignWithApplicant() {
      if (this.itemToBeDeleted.id) {
        const id = this.itemToBeDeleted.id;
        EventBus.$emit('waiting', true);

        this.axios
          .delete(`api/v1/campaign/${id}`)
          .then(response => {
            console.log('Campaign deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Deleted Successfully',
            });
            this.deleteDialog = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error deleting campaign:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },

    copyCampaignData(item) {
      if (item) {
        const id = item.id;
        EventBus.$emit('waiting', true);

        this.axios
          .post(`api/v1/campaign/copy?campaignIdToCopy=${id}`)
          .then(response => {
            console.log('Campaign copied successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Campaign Copied Successfully',
            });
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error copying campaign:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    unpublishCampaignFromEdit() {
      if (this.itemForEdit.id && !this.hasApplicants) {
        const id = this.itemForEdit.id;
        EventBus.$emit('waiting', true);

        const payload = {
          status: 'DRAFT',
        };

        this.axios
          .put(`/api/v1/campaign/${id}`, payload)
          .then(response => {
            console.log('Response:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Unpublished Successfully',
            });
            this.editDialogWhenPublished = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.log('Error:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
  },
};
</script>
